import React from 'react';

import {
  getValidationErrorMessageFor,
  isValidationError,
} from '~/api/api-errors';
import Button from '~/components/button';
import { Field, Form } from '~/components/form';
import InputField from '~/components/inputs/input-field';
import Modal from '~/components/modals/modal';
import { toApiPhone } from '~/features/addresses/address';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { smsSubscribe } from '~/features/sms/sms-actions';
import { SmsSource } from '~/features/sms/sms';
import { useUpdateShippingAddress } from '~/features/user/user-actions';
import { useMediaQuery } from '~/hooks/use-media-query';
import { termsRoute } from '~/routes';
import { captureException } from '~/utils/exception-tracking';
import { PHONE_MASK } from '~/utils/input-masks';
import { formatPhoneNumber } from '~/utils/phoneNumbers';
import { phone, required, valid } from '~/utils/validation';

import styles from '~/components/modals/sms/sms-subscribe-modal.module.scss';
import modalStyles from '~/components/modals/modal.module.scss';

const SmsSubscribeModal = ({ isOpen, onClose, defaultShippingAddress }) => {
  if (!defaultShippingAddress && isOpen) {
    captureException(
      new Error('No default shipping address when subscribing to SMS')
    );

    return <MissingDefaultAddressModal isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <SmsSubscribeModalContent
        defaultShippingAddress={defaultShippingAddress}
        onClose={onClose}
      />
    </Modal>
  );
};

const SmsSubscribeModalContent = ({ defaultShippingAddress, onClose }) => {
  const { showFlashMessage } = useFlashMessage();
  const { updateShippingAddress } = useUpdateShippingAddress();
  const isDesktop = useMediaQuery('(min-width: 992px)');

  const subscribeToSms = async () => {
    try {
      await smsSubscribe(SmsSource.MY_PLAN);

      onClose();
      showFlashMessage(
        'You have successfully signed up for text reminders!',
        'success'
      );
    } catch (err) {
      showFlashMessage(
        'Sorry, there was a problem signing you up for texts. Please try again later.',
        'error'
      );
      captureException(new Error('Error subscribing to SMS on My Plan page'), {
        extras: {
          error: err,
          errorString: JSON.stringify(err),
          addressUuid: defaultShippingAddress.uuid,
        },
      });
    }
  };

  const updateDefaultShippingAddress = async (formValues) => {
    try {
      await updateShippingAddress({
        ...defaultShippingAddress,
        addressUuid: defaultShippingAddress.uuid,
        phoneNumber: toApiPhone(formValues.phone),
      });
    } catch (err) {
      if (isValidationError(err)) {
        return {
          phone: getValidationErrorMessageFor(err, 'phoneNumber'),
        };
      } else {
        showFlashMessage(
          'Sorry, there was a problem signing you up for texts. Please try again later.',
          'error'
        );
        captureException(
          new Error(
            'Error updating default address for SMS sign up on My Plan page'
          ),
          {
            extras: {
              error: err,
              errorString: JSON.stringify(err),
              addressUuid: defaultShippingAddress.uuid,
              phoneNumber: toApiPhone(formValues.phone),
            },
          }
        );
      }

      // we don't want to proceed with subscribing to SMS if there was an issue updating
      // the customer's default shipping address because the BE pulls the phone number from the
      // default shipping address when subscribing to SMS
      return;
    }

    subscribeToSms();
  };

  const handleSubmit = async (formValues) => {
    // only call updateShippingAddress if the phone number has changed
    // otherwise, just call subscribeSMS
    // this is to prevent an unnecessary API call to update the shipping address
    if (
      formatPhoneNumber(defaultShippingAddress.phoneNumber) !== formValues.phone
    ) {
      updateDefaultShippingAddress(formValues);
    } else {
      subscribeToSms();
    }
  };

  return (
    <div className={styles.smsSubscribeModal}>
      <h2 className={styles.title}>
        Get text reminders when it's time to apply!
      </h2>

      <Form
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={{
          phone: formatPhoneNumber(defaultShippingAddress?.phoneNumber) || '',
        }}
      >
        {({ isSubmitting }) => (
          <section className={styles.smsSubscribeForm}>
            <div className={styles.phoneNumberField}>
              <Field
                label="Mobile number"
                name="phone"
                type="tel"
                component={InputField}
                autoComplete="tel-national"
                mask={PHONE_MASK}
                placeholder="(___) ___-____"
              />
            </div>
            <Button
              fullWidth
              variant="primary"
              type="submit"
              isLoading={isSubmitting}
              data-heap-id="sms-my-plan-submit"
            >
              Sign me up!
            </Button>
          </section>
        )}
      </Form>

      <p className={styles.finePrint}>
        By {isDesktop ? 'clicking' : 'tapping'} 'Sign me up!', you agree to
        receive recurring automated promotional and personalized marketing text
        messages (e.g. cart reminders) from Sunday at the cell number used when
        signing up. Consent is not a condition of any purchase. Reply HELP for
        help and STOP to cancel. Msg frequency varies. Msg & data rates may
        apply. View{' '}
        <a
          href={termsRoute()}
          aria-label="Terms"
          title="Terms"
          target="_blank"
          rel="noreferrer"
        >
          Terms
        </a>{' '}
        &amp;{' '}
        <a
          href="https://www.iubenda.com/privacy-policy/87446194/full-legal"
          aria-label="Privacy"
          title="Privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy
        </a>
        .
      </p>
    </div>
  );
};

export default SmsSubscribeModal;

const validate = (formValues) => {
  return {
    phone: valid(required(), phone())(formValues.phone),
  };
};

const MissingDefaultAddressModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div>
        <h1 className={modalStyles.title}>Oops.</h1>
        <p className={styles.authFailModalNoCartMessage}>
          Please update your default shipping address to opt into text
          reminders.
        </p>
        <Button fullWidth onClick={() => onClose()}>
          Close
        </Button>
      </div>
    </Modal>
  );
};
